// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AdminDiscord.js
// Admin: Discord bot settings (market.discord_settings), a free-text message
// box, end-of-day reports (market.discord_reports) and recent alerts.
// Saving never overwrites - it adds a row, timestamped, which becomes live
// for the next announcement (read by the jobs-side discord_notify.py).
// Layout, styles and admin guard follow AdminPrompts.js; the API is
// @admin_required (admin.py).
// Alert grid: rows are significance 5-1, columns the AI novelty label (first,
// update, repeat, reversal, or not labelled) plus Thematic - any announcement
// carrying at least one thematic, whatever its novelty. An announcement posts
// when one of its cells is ticked and its direction is ticked. The row's box
// ticks the whole row. A setting saved before the grid existed is shown as the
// cells it meant (its minimum significance and above, every column).
// Expected move and surprise: further filters on top of the grid and directions -
// only the ticked bands / levels post ("Not given" = a summary without one).
// All ticked is saved as "any" (NULL), which is also how older settings show.
// Reports: saved scanner-style rules, run for ASX or US after that exchange's
// EOD run by the jobs-side discord_reports.py. The rules use scan_runner.py's
// format (snapshot_filters / bar_conditions, same keys and defaults as the
// scans workspace) plus asset_types; Test shows the matches without posting.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import NoveltyTag from '../components/NoveltyTag';
import ImpactTags from '../components/ImpactTags';
import ReactionTag from '../components/ReactionTag';
import ThematicTags from '../components/ThematicTags';
import api from '../api';

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1100px', margin: '0 auto', padding: '40px 32px' },
  heading: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '8px' },
  sub:     { fontSize: '13px', color: 'var(--text-muted)', marginBottom: '32px' },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '24px', marginBottom: '24px' },
  title:   { fontSize: '14px', fontWeight: 600, marginBottom: '4px', color: 'var(--text)' },
  meta:    { fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' },
  btn:     { padding: '6px 14px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 500, fontFamily: "'Space Grotesk', sans-serif" },
  err:     { color: 'var(--error)', fontSize: '13px', marginTop: '8px' },
  ok:      { color: 'var(--success)', fontSize: '13px', marginTop: '8px' },
  area:    {
    width: '100%', minHeight: '100px', boxSizing: 'border-box', resize: 'vertical',
    fontFamily: "'Space Grotesk', sans-serif", fontSize: '13px', lineHeight: 1.5,
    padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
    background: 'var(--bg)', color: 'var(--text)', outline: 'none',
  },
  hist:    { fontSize: '12px', padding: '4px 0', display: 'flex', justifyContent: 'space-between', gap: '12px' },
  label:   { fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' },
  select:  {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '13px',
    fontFamily: "'Space Grotesk', sans-serif", border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', outline: 'none',
  },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:      { textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontWeight: 500, fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.05em', textTransform: 'uppercase' },
  td:      { padding: '6px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
};

const when = iso => new Date(iso).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

// Directions that can post automatically. "mixed" also covers neutral, as elsewhere on the site.
const DIRECTIONS = [
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
  { value: 'mixed',    label: 'Mixed / neutral' },
];
const ARROWS = { positive: '▲', negative: '▼', mixed: '◆', neutral: '◆' };   // as in the Discord alerts

// Alert grid: significance rows, novelty columns plus Thematic. Cell ids "4:first", "4:thematic" etc. match admin.py.
const SIGS    = [5, 4, 3, 2, 1];
const NOVELTY = [
  { value: 'first',    label: 'First' },
  { value: 'update',   label: 'Update' },
  { value: 'repeat',   label: 'Repeat' },
  { value: 'reversal', label: 'Reversal' },
  { value: 'none',     label: 'Not labelled', title: 'Older summaries, or the AI gave no novelty label' },
  { value: 'thematic', label: 'Thematic',     title: 'Carries at least one thematic (AI Prompts page), whatever its novelty' },
];
// Expected-move and surprise filters - values match admin.py (_DISCORD_MOVES / _DISCORD_SURPRISES).
const MOVES = [
  { value: '<2', label: '<2%' }, { value: '2-5', label: '2-5%' }, { value: '5-10', label: '5-10%' },
  { value: '10-20', label: '10-20%' }, { value: '20-50', label: '20-50%' }, { value: '50+', label: '50%+' },
  { value: 'none', label: 'Not given', title: 'Older summaries, or the AI gave no expected move' },
];
const SURPRISES = [
  { value: 'much better', label: 'Much better' }, { value: 'better', label: 'Better' },
  { value: 'as expected', label: 'As expected' }, { value: 'worse', label: 'Worse' },
  { value: 'much worse', label: 'Much worse' },
  { value: 'none', label: 'Not given', title: 'Older summaries, or the AI gave no surprise level' },
];
const allOf = list => list.map(x => x.value);
const filterText = (list, vs) => !vs ? 'any' : vs.length ? list.filter(x => vs.includes(x.value)).map(x => x.label).join(', ') : 'none';
const cell = (n, v) => `${n}:${v}`;
// A setting's cells - a pre-grid setting meant "min_significance and above", any novelty.
const cellsOf = v => v?.cells ?? (v ? SIGS.filter(n => n >= v.min_significance).flatMap(n => NOVELTY.map(c => cell(n, c.value))) : []);

const dirNames = ds => ds.length ? DIRECTIONS.filter(d => ds.includes(d.value)).map(d => d.label).join(', ') : 'none';
// "5, 4 all · 3 first, reversal" - a setting's grid in words.
const gridText = cs => {
  const parts = SIGS.map(n => {
    const on = NOVELTY.filter(c => cs.includes(cell(n, c.value)));
    return !on.length ? null : on.length === NOVELTY.length ? `${n} all` : `${n} ${on.map(c => c.label.toLowerCase()).join(', ')}`;
  }).filter(Boolean);
  return parts.length ? parts.join(' · ') : 'nothing';
};
const setting = v => `${v.enabled ? 'On' : 'Off'} · ${gridText(cellsOf(v))} · ${dirNames(v.directions || [])}`
  + `${v.moves ? ` · move ${filterText(MOVES, v.moves)}` : ''}${v.surprises ? ` · surprise ${filterText(SURPRISES, v.surprises)}` : ''}`;
const sameSet = (a = [], b = []) => a.length === b.length && a.every(x => b.includes(x));
const dots = n => '●'.repeat(n) + '○'.repeat(5 - n);                          // as in the Discord alerts

// Grid column heading: the tag as it appears on the site, or plain text for "not labelled".
const colHead = c => c.value === 'none' ? c.label
                   : c.value === 'thematic' ? <ThematicTags t={[c.label]} /> : <NoveltyTag n={c.value} />;

// Announcement alerts: on/off, the significance x novelty grid, directions, test message.
function AlertSettings({ data, onSaved }) {
  const live = data.history[0];
  const [enabled, setEnabled] = useState(live?.enabled ?? true);
  const [cells, setCells]     = useState(cellsOf(live));
  const [dirs, setDirs]       = useState(live?.directions ?? ['positive']);
  const [moves, setMoves]     = useState(live?.moves ?? allOf(MOVES));
  const [surps, setSurps]     = useState(live?.surprises ?? allOf(SURPRISES));
  const [minAlert, setMinAlert] = useState(live?.min_alert ?? '');   // reaction model: chance of a 5%+ rise, 0-100; '' = off
  const [minRisk, setMinRisk]   = useState(live?.min_risk ?? '');    // ... of a 5%+ fall
  const [useGrid, setUseGrid]   = useState(live?.use_grid ?? true);  // also post on the document rules below
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState('');
  const [err, setErr]         = useState('');

  useEffect(() => {
    setEnabled(live?.enabled ?? true); setCells(cellsOf(live)); setDirs(live?.directions ?? ['positive']);
    setMoves(live?.moves ?? allOf(MOVES)); setSurps(live?.surprises ?? allOf(SURPRISES));
    setMinAlert(live?.min_alert ?? ''); setMinRisk(live?.min_risk ?? ''); setUseGrid(live?.use_grid ?? true);
  }, [live]);

  const num = v => (v === '' || v === null || v === undefined ? null : Number(v));
  const changed = enabled !== live?.enabled || !sameSet(cells, cellsOf(live)) || !sameSet(dirs, live?.directions)
                  || !sameSet(moves, live?.moves ?? allOf(MOVES)) || !sameSet(surps, live?.surprises ?? allOf(SURPRISES))
                  || num(minAlert) !== (live?.min_alert ?? null) || num(minRisk) !== (live?.min_risk ?? null)
                  || useGrid !== (live?.use_grid ?? true);
  const flip = set => v => set(vs => vs.includes(v) ? vs.filter(x => x !== v) : [...vs, v]);
  const toggleDir = flip(setDirs);
  const toggleCell = c => setCells(cs => cs.includes(c) ? cs.filter(x => x !== c) : [...cs, c]);
  const rowCells = n => NOVELTY.map(c => cell(n, c.value));
  const rowAll = n => rowCells(n).every(c => cells.includes(c));
  const toggleRow = n => setCells(cs => rowAll(n) ? cs.filter(c => !rowCells(n).includes(c))
                                                  : [...new Set([...cs, ...rowCells(n)])]);

  const run = async (call) => {
    setBusy(true); setMsg(''); setErr('');
    try { setMsg(await call()); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const save = () => run(async () => {
    const r = await api.post('/admin/discord-settings', { enabled, cells, directions: dirs, moves, surprises: surps,
                                                          min_alert: num(minAlert), min_risk: num(minRisk), use_grid: useGrid });
    onSaved();
    return `${r.data.message} (${when(r.data.created_at)}).`;
  });

  const test = () => run(async () => (await api.post('/admin/discord-test')).data.message);

  const gc = { padding: '5px 10px', textAlign: 'center', borderBottom: '1px solid var(--border)' };

  return (
    <div style={s.section}>
      <div style={s.title}>Announcement alerts</div>
      <div style={s.meta}>
        Posts fresh announcements, from every source, the moment the AI summary is saved - when the Logic Tree's
        chances clear the thresholds below, or (if the document rules are on) when one of its cells is ticked (its
        importance and novelty, or its importance and Thematic if it carries a thematic) and its direction, expected
        move and surprise are ticked. Applies to the next announcement - no restart needed.<br />
        {live ? `Live since ${when(live.created_at)}${live.created_by ? ` by ${live.created_by}` : ''}` : 'No setting saved yet.'}
      </div>

      <div style={{ maxWidth: '260px' }}>
        <div style={s.label}>Alerts</div>
        <select style={s.select} value={enabled ? 'on' : 'off'} onChange={e => setEnabled(e.target.value === 'on')}>
          <option value="on">On</option>
          <option value="off">Off</option>
        </select>
      </div>

      <div style={{ marginTop: '14px' }}>
        <div style={s.label}>Logic Tree - post when the chance of a 5%+ move is at least (0-100, blank = off)</div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <label>Rise (alert score) <input type="number" min="1" max="100" style={{ ...s.select, width: '80px' }} value={minAlert}
                 onChange={e => setMinAlert(e.target.value)} /></label>
          <label>Fall (risk score) <input type="number" min="1" max="100" style={{ ...s.select, width: '80px' }} value={minRisk}
                 onChange={e => setMinRisk(e.target.value)} /></label>
          <label><input type="checkbox" checked={useGrid} onChange={e => setUseGrid(e.target.checked)} /> Also post on the document rules below</label>
        </div>
        <div style={s.meta}>The chances are calibrated: 30 means about 30 in 100 such announcements moved 5% or more that way; an average
          announcement is about 10. The Logic Tree table on AI Stats shows how many each level would have posted and how they did.</div>
      </div>

      <div style={{ marginTop: '14px' }}>
        <div style={s.label}>Which announcements - importance by novelty</div>
        <table style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr>
              <th style={{ ...s.th, textAlign: 'left' }}>Importance</th>
              {NOVELTY.map(c => (
                <th key={c.value} style={{ ...s.th, textAlign: 'center' }} title={c.title}>{colHead(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SIGS.map(n => (
              <tr key={n}>
                <td style={{ ...gc, textAlign: 'left', whiteSpace: 'nowrap' }}>
                  <label style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }} title="Tick or clear the whole row">
                    <input type="checkbox" checked={rowAll(n)} onChange={() => toggleRow(n)} />
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--accent)' }}>{dots(n)}</span> {n}
                  </label>
                </td>
                {NOVELTY.map(c => (
                  <td key={c.value} style={gc}>
                    <input type="checkbox" checked={cells.includes(cell(n, c.value))} onChange={() => toggleCell(cell(n, c.value))} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ ...s.meta, marginTop: '6px', marginBottom: 0 }}>Ticked: {gridText(cells)}</div>
      </div>

      <div style={{ marginTop: '12px' }}>
        <div style={s.label}>Directions</div>
        <div style={{ display: 'flex', gap: '18px', fontSize: '13px' }}>
          {DIRECTIONS.map(d => (
            <label key={d.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={dirs.includes(d.value)} onChange={() => toggleDir(d.value)} />
              {d.label}
            </label>
          ))}
        </div>
      </div>

      {[['Expected move (either direction)', MOVES, moves, flip(setMoves)],
        ['Surprise against expectations', SURPRISES, surps, flip(setSurps)]].map(([label, list, vs, toggle]) => (
        <div key={label} style={{ marginTop: '12px' }}>
          <div style={s.label}>{label}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: '13px' }}>
            {list.map(x => (
              <label key={x.value} title={x.title} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input type="checkbox" checked={vs.includes(x.value)} onChange={() => toggle(x.value)} />
                {x.label}
              </label>
            ))}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
        <button onClick={save} disabled={!changed || busy}
                style={{ ...s.btn, background: changed ? 'var(--accent)' : 'var(--border)', color: changed ? 'var(--on-accent)' : 'var(--text-muted)', cursor: changed ? 'pointer' : 'default' }}>
          {busy ? 'Working…' : 'Save and make live'}
        </button>
        <button onClick={test} disabled={busy}
                style={{ ...s.btn, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)' }}>
          Send test message
        </button>
      </div>
      {msg && <div style={s.ok}>{msg}</div>}
      {err && <div style={s.err}>✕ {err}</div>}
      {data.failed > 0 && (
        <div style={s.err}>
          {data.failed} alert{data.failed > 1 ? 's' : ''} failed to send in the last 24 hours - resent after the next fresh announcement.
        </div>
      )}

      {data.history.length > 1 && (
        <details style={{ marginTop: '16px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--text-muted)' }}>
            Earlier settings ({data.history.length - 1})
          </summary>
          {data.history.slice(1).map(v => (
            <div key={v.created_at} style={s.hist}>
              <span>{when(v.created_at)}{v.created_by ? ` · ${v.created_by}` : ''}</span>
              <span>{setting(v)}</span>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

// Free-text message from the bot to the alerts channel.
function SendMessage() {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState('');
  const [err, setErr]   = useState('');

  const send = async () => {
    setBusy(true); setMsg(''); setErr('');
    try {
      const r = await api.post('/admin/discord-message', { text });
      setMsg(r.data.message); setText('');
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const ready = text.trim() && !busy;
  return (
    <div style={s.section}>
      <div style={s.title}>Send a message</div>
      <div style={s.meta}>Posted to the alerts channel as Woody Bot. Up to 2000 characters; Discord markdown works.</div>
      <textarea style={s.area} value={text} maxLength={2000} onChange={e => setText(e.target.value)} />
      <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
        <button onClick={send} disabled={!ready}
                style={{ ...s.btn, background: ready ? 'var(--accent)' : 'var(--border)', color: ready ? 'var(--on-accent)' : 'var(--text-muted)', cursor: ready ? 'pointer' : 'default' }}>
          {busy ? 'Sending…' : 'Send'}
        </button>
      </div>
      {msg && <div style={s.ok}>{msg}</div>}
      {err && <div style={s.err}>✕ {err}</div>}
    </div>
  );
}

// Alerts posted recently, newest first.
function RecentAlerts({ data }) {
  return (
    <div style={s.section}>
      <div style={s.title}>Sent alerts</div>
      <div style={s.meta}>Announcements from the last {data.recent_days} days that were posted to Discord.</div>
      {data.recent.length === 0 ? (
        <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>None yet.</div>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Sent</th>
              <th style={s.th}>Ticker</th>
              <th style={s.th}>Sig</th>
              <th style={s.th}>Announcement</th>
            </tr>
          </thead>
          <tbody>
            {data.recent.map(r => (
              <tr key={`${r.discord_sent_at}${r.tickers}`}>
                <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{when(r.discord_sent_at)}</td>
                <td style={s.td}>
                  {r.tickers.split(', ').map(t => (
                    <a key={t} href={`/company?ticker=${encodeURIComponent(t)}`} style={{ color: 'var(--accent)', marginRight: '6px' }}>{t}</a>
                  ))}
                </td>
                <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                  {ARROWS[r.direction] || ''} {dots(r.significance)}<NoveltyTag n={r.novelty} />
                  <ReactionTag model={r.reaction ? { reaction: r.reaction, band: r.reaction_band, confidence: r.confidence, alert: r.alert_score, risk: r.risk_score } : null} /><ImpactTags move={r.expected_move} surprise={r.surprise} positioning={r.positioning} financing={r.financing_risk} /><ThematicTags t={r.thematics} />
                </td>
                <td style={s.td}>{r.headline}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


// ── End-of-day reports ───────────────────────────────────────────────────────
// Field keys and defaults are scan_runner.py's (see its docstring).
const EXCHANGES   = { AU: 'ASX', US: 'US' };
const EMA_OPTIONS = ['8', '9', '12', '20', '21', '26', '50', '70', '100', '150', '200'];   // ingest_eod_bars EMA_PERIODS
const NUM_FILTERS = [
  ['mcap_min', 'Market cap min ($M)'], ['mcap_max', 'Market cap max ($M)'],
  ['price_min', 'Price min'],          ['price_max', 'Price max'],
  ['rsi_min', 'RSI 14 min'],           ['rsi_max', 'RSI 14 max'],
  ['stoch_min', 'Stochastic %K min'],  ['stoch_max', 'Stochastic %K max'],
  ['volume_min', 'Volume min'],        ['atr_pct_min', 'ATR % of price min'],
  ['yield_min', 'Dividend yield % min'], ['yield_max', 'Dividend yield % max'],
];
// Price-action conditions on the latest candle(s). 52-week proximity is left
// out until scan_runner.py's version is fixed (it never matches).
const CONDITIONS = [
  { key: 'vol_spike', label: 'Volume spike', params: [
    { k: 'pct', label: 'Volume ≥ % of average', def: 150 }, { k: 'days', label: 'Average over days', def: 20 }] },
  { key: 'big_green', label: 'Big green candle', params: [
    { k: 'body_pct', label: 'Body ≥ % of range', def: 60 }, { k: 'avg_days', label: 'Bigger than average body of days', def: 10 }] },
  { key: 'range_expansion', label: 'Range expansion', params: [
    { k: 'pct', label: "Range ≥ % bigger than yesterday's", def: 50 }] },
  { key: 'gap', label: 'Gap', params: [
    { k: 'direction', label: 'Direction', opts: ['up', 'down', 'either'], def: 'up' }, { k: 'pct', label: 'Gap ≥ %', def: 2 }] },
  { key: 'inside_outside_bar', label: 'Inside / outside bar', params: [
    { k: 'type', label: 'Type', opts: ['inside', 'outside'], def: 'outside' }] },
  { key: 'n_day_breakout', label: 'N-day breakout', params: [
    { k: 'direction', label: 'Close breaks the', opts: ['high', 'low'], def: 'high' }, { k: 'days', label: 'Days', def: 52 }] },
  { key: 'consecutive_closes', label: 'Consecutive closes', params: [
    { k: 'direction', label: 'Direction', opts: ['up', 'down'], def: 'up' }, { k: 'count', label: 'Days in a row', def: 3 }] },
  { key: 'ema_slope', label: 'EMA slope', params: [
    { k: 'ema', label: 'EMA', opts: EMA_OPTIONS, def: '50' }, { k: 'direction', label: 'Direction', opts: ['rising', 'falling'], def: 'rising' },
    { k: 'days', label: 'Over days', def: 5 }] },
];
const BLANK = { name: '', exchange: 'AU', intro: '', enabled: true,
                rules: { snapshot_filters: {}, bar_conditions: {}, asset_types: ['equity', 'etf'] } };

const rs = {
  grid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '10px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif",
           border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', outline: 'none' },
  sub:   { fontSize: '12px', fontWeight: 600, margin: '18px 0 8px', color: 'var(--text)' },
  row:   { display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px', fontSize: '12px' },
  small: { ...s.btn, padding: '3px 8px', fontSize: '11px', background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)' },
  link:  { color: 'var(--accent)', cursor: 'pointer', marginRight: '10px', fontSize: '12px' },
  cond:  { border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 10px' },
};

// The editor for one report (new or loaded).
function ReportEditor({ initial, onSaved, onCancel }) {
  const [r, setR]       = useState(initial);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState('');
  const [err, setErr]   = useState('');
  const [preview, setPreview] = useState(null);    // { count, tickers }

  const sf = r.rules.snapshot_filters || {};
  const bc = r.rules.bar_conditions || {};
  const types = r.rules.asset_types || ['equity', 'etf'];
  const setRules = patch => { setR(x => ({ ...x, rules: { ...x.rules, ...patch } })); setPreview(null); };
  const setSf = (k, v) => {                          // blank removes the filter
    const next = { ...sf };
    if (v === '' || v == null) delete next[k]; else next[k] = v;
    setRules({ snapshot_filters: next });
  };
  const rules = (k, list) => setSf(k, list.length ? list : null);
  const setCond = (key, patch) => {                  // patch null = switch the condition off
    const next = { ...bc };
    if (patch === null) delete next[key]; else next[key] = { ...(next[key] || {}), ...patch };
    setRules({ bar_conditions: next });
  };
  const toggleType = t => setRules({ asset_types: types.includes(t) ? types.filter(x => x !== t) : [...types, t] });

  const run = async (call) => {
    setBusy(true); setMsg(''); setErr('');
    try { await call(); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };
  const test = () => run(async () => {
    const res = await api.post('/admin/discord-reports/preview', { exchange: r.exchange, rules: r.rules });
    setPreview(res.data);
  });
  const save = () => run(async () => {
    const body = { name: r.name, exchange: r.exchange, intro: r.intro, enabled: r.enabled, rules: r.rules };
    const res = r.id ? await api.put(`/admin/discord-reports/${r.id}`, body) : await api.post('/admin/discord-reports', body);
    setR(x => ({ ...x, id: res.data.id }));
    setMsg(res.data.message);
    onSaved();
  });

  const ready = r.name.trim() && r.intro.trim() && types.length && !busy;
  const numInput = (k, value, onChange) => (
    <input style={rs.input} type="number" step="any" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
  );

  return (
    <div style={{ ...s.section, borderColor: 'var(--accent)' }}>
      <div style={s.title}>{r.id ? `Edit report: ${initial.name}` : 'New report'}</div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', marginTop: '10px' }}>
        <div>
          <div style={s.label}>Name (for this page)</div>
          <input style={rs.input} value={r.name} onChange={e => setR(x => ({ ...x, name: e.target.value }))} />
        </div>
        <div>
          <div style={s.label}>Exchange - runs after its EOD run</div>
          <select style={s.select} value={r.exchange} onChange={e => { setR(x => ({ ...x, exchange: e.target.value })); setPreview(null); }}>
            {Object.entries(EXCHANGES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginTop: '10px' }}>
        <div style={s.label}>Opening text - the matching tickers follow it</div>
        <input style={rs.input} maxLength={500} value={r.intro} placeholder="Volatility breakouts for today's ASX:"
               onChange={e => setR(x => ({ ...x, intro: e.target.value }))} />
      </div>
      <div style={{ ...rs.row, marginTop: '10px', gap: '16px', fontSize: '13px' }}>
        {[['equity', 'Stocks'], ['etf', 'ETFs']].map(([t, l]) => (
          <label key={t} style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={types.includes(t)} onChange={() => toggleType(t)} />{l}
          </label>
        ))}
        <label style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={r.enabled} onChange={e => setR(x => ({ ...x, enabled: e.target.checked }))} />Report on
        </label>
      </div>

      <div style={rs.sub}>Filters (latest close)</div>
      <div style={rs.grid}>
        {NUM_FILTERS.map(([k, label]) => (
          <div key={k}><div style={s.label}>{label}</div>{numInput(k, sf[k], v => setSf(k, v))}</div>
        ))}
        <div>
          <div style={s.label}>Bollinger bands</div>
          <select style={s.select} value={sf.bb_position || ''} onChange={e => setSf('bb_position', e.target.value)}>
            <option value="">Any</option><option value="above_upper">Closed above upper band</option>
            <option value="below_lower">Closed below lower band</option><option value="inside">Inside the bands</option>
          </select>
        </div>
        <div>
          <div style={s.label}>OBV</div>
          <select style={s.select} value={sf.obv_direction || ''}
                  onChange={e => { setSf('obv_direction', e.target.value); }}>
            <option value="">Any</option><option value="rising">Rising</option><option value="falling">Falling</option>
          </select>
        </div>
        {sf.obv_direction && (
          <div><div style={s.label}>OBV over days</div>{numInput('obv_days', sf.obv_days ?? 10, v => setSf('obv_days', v))}</div>
        )}
      </div>

      <div style={rs.sub}>EMA rules <span style={rs.link} onClick={() => rules('ema_rules', [...(sf.ema_rules || []), { ema1: '50', dir: 'above', ema2: '200' }])}>+ add</span></div>
      {(sf.ema_rules || []).map((x, i) => {
        const upd = patch => rules('ema_rules', sf.ema_rules.map((y, j) => j === i ? { ...y, ...patch } : y));
        return (
          <div key={i} style={rs.row}>
            <select style={{ ...s.select, width: 'auto' }} value={x.ema1} onChange={e => upd({ ema1: e.target.value })}>{EMA_OPTIONS.map(o => <option key={o} value={o}>EMA {o}</option>)}</select>
            <select style={{ ...s.select, width: 'auto' }} value={x.dir} onChange={e => upd({ dir: e.target.value })}><option value="above">above</option><option value="below">below</option></select>
            <select style={{ ...s.select, width: 'auto' }} value={x.ema2} onChange={e => upd({ ema2: e.target.value })}>{EMA_OPTIONS.map(o => <option key={o} value={o}>EMA {o}</option>)}</select>
            <button style={rs.small} onClick={() => rules('ema_rules', sf.ema_rules.filter((_, j) => j !== i))}>✕</button>
          </div>
        );
      })}
      <div style={rs.sub}>Price vs EMA rules <span style={rs.link} onClick={() => rules('price_ema_rules', [...(sf.price_ema_rules || []), { dir: 'above', ema: '50' }])}>+ add</span></div>
      {(sf.price_ema_rules || []).map((x, i) => {
        const upd = patch => rules('price_ema_rules', sf.price_ema_rules.map((y, j) => j === i ? { ...y, ...patch } : y));
        return (
          <div key={i} style={rs.row}>
            <span>Price</span>
            <select style={{ ...s.select, width: 'auto' }} value={x.dir} onChange={e => upd({ dir: e.target.value })}><option value="above">above</option><option value="below">below</option></select>
            <select style={{ ...s.select, width: 'auto' }} value={x.ema} onChange={e => upd({ ema: e.target.value })}>{EMA_OPTIONS.map(o => <option key={o} value={o}>EMA {o}</option>)}</select>
            <button style={rs.small} onClick={() => rules('price_ema_rules', sf.price_ema_rules.filter((_, j) => j !== i))}>✕</button>
          </div>
        );
      })}

      <div style={rs.sub}>Price action (latest candle) - every ticked condition must hold</div>
      <div style={{ ...rs.grid, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
        {CONDITIONS.map(c => {
          const on = !!bc[c.key];
          return (
            <div key={c.key} style={{ ...rs.cond, ...(on && { borderColor: 'var(--accent)' }) }}>
              <label style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer', fontSize: '13px' }}>
                <input type="checkbox" checked={on}
                       onChange={() => setCond(c.key, on ? null : { enabled: true, ...Object.fromEntries(c.params.map(p => [p.k, p.def])) })} />
                {c.label}
              </label>
              {on && c.params.map(p => (
                <div key={p.k} style={{ marginTop: '6px' }}>
                  <div style={s.label}>{p.label}</div>
                  {p.opts
                    ? <select style={s.select} value={bc[c.key][p.k]} onChange={e => setCond(c.key, { [p.k]: e.target.value })}>
                        {p.opts.map(o => <option key={o} value={o}>{c.key === 'ema_slope' && p.k === 'ema' ? `EMA ${o}` : o}</option>)}
                      </select>
                    : numInput(p.k, bc[c.key][p.k], v => setCond(c.key, { [p.k]: v === '' ? p.def : v }))}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button onClick={test} disabled={busy || !types.length} style={{ ...s.btn, background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)' }}>
          {busy ? 'Working…' : 'Test - show matches'}
        </button>
        <button onClick={save} disabled={!ready}
                style={{ ...s.btn, background: ready ? 'var(--accent)' : 'var(--border)', color: ready ? 'var(--on-accent)' : 'var(--text-muted)', cursor: ready ? 'pointer' : 'default' }}>
          Save report
        </button>
        <button onClick={onCancel} style={{ ...s.btn, background: 'none', color: 'var(--text-muted)' }}>Close</button>
      </div>
      {msg && <div style={s.ok}>{msg}</div>}
      {err && <div style={s.err}>✕ {err}</div>}
      {preview && (
        <div style={{ marginTop: '12px', fontSize: '13px' }}>
          <div style={s.label}>{preview.count} match{preview.count === 1 ? '' : 'es'} on the latest close - the message would read:</div>
          <div style={{ ...rs.cond, background: 'var(--bg)', whiteSpace: 'pre-wrap' }}>
            {r.intro || '(opening text)'} {preview.count ? preview.tickers.join(', ') : 'No stocks match today'}
          </div>
        </div>
      )}
    </div>
  );
}

// Saved reports: list with on/off, edit, run now and delete, plus the editor.
function Reports() {
  const [list, setList]       = useState(null);
  const [editing, setEditing] = useState(null);    // report being edited, or BLANK
  const [msg, setMsg]         = useState('');
  const [err, setErr]         = useState('');

  const load = useCallback(async () => {
    try { setList((await api.get('/admin/discord-reports')).data.reports); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (call) => {
    setMsg(''); setErr('');
    try { setMsg((await call()).data.message); load(); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
  };
  const toggle = r => act(() => api.put(`/admin/discord-reports/${r.id}`,
    { name: r.name, exchange: r.exchange, intro: r.intro, rules: r.rules, enabled: !r.enabled }));
  const runNow = r => act(() => api.post(`/admin/discord-reports/${r.id}/run`));
  const remove = r => window.confirm(`Delete the report "${r.name}"?`) && act(() => api.delete(`/admin/discord-reports/${r.id}`));

  return (
    <>
      <div style={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={s.title}>End-of-day reports</div>
          <span style={rs.link} onClick={() => setEditing({ ...BLANK })}>+ New report</span>
        </div>
        <div style={s.meta}>
          Each switched-on report posts after its exchange's EOD run: the opening text, then the tickers that match.
          Run now posts it straight away from the latest close.
        </div>
        {!list ? <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Loading…</div>
        : list.length === 0 ? <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No reports yet.</div>
        : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>On</th><th style={s.th}>Name</th><th style={s.th}>Exchange</th>
                <th style={s.th}>Opening text</th><th style={s.th}>Last run</th><th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {list.map(r => (
                <tr key={r.id}>
                  <td style={s.td}><input type="checkbox" checked={r.enabled} onChange={() => toggle(r)} /></td>
                  <td style={s.td}>{r.name}</td>
                  <td style={s.td}>{EXCHANGES[r.exchange]}</td>
                  <td style={s.td}>{r.intro}</td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{r.last_run_at ? `${when(r.last_run_at)} · ${r.last_count}` : '—'}</td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                    <span style={rs.link} onClick={() => setEditing(r)}>Edit</span>
                    <span style={rs.link} onClick={() => runNow(r)}>Run now</span>
                    <span style={{ ...rs.link, color: 'var(--error)' }} onClick={() => remove(r)}>Delete</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {msg && <div style={s.ok}>{msg}</div>}
        {err && <div style={s.err}>✕ {err}</div>}
      </div>
      {editing && <ReportEditor key={editing.id || 'new'} initial={editing} onSaved={load} onCancel={() => setEditing(null)} />}
    </>
  );
}

export default function AdminDiscord() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr]   = useState('');

  useEffect(() => {
    if (user && !user.is_admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await api.get('/admin/discord-settings');
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={s.page}>
      <Navbar backTo={{ label: '← Admin', href: '/admin' }} />
      <div style={s.content}>
        <h1 style={s.heading}>Discord Bot</h1>
        <p style={s.sub}>
          What Woody Bot posts to the Discord alerts channel. Saving adds a new setting and makes it live;
          earlier settings are listed below it.
        </p>
        {err && <div style={s.err}>✕ {err}</div>}
        {!data && !err && <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</div>}
        {data && <AlertSettings data={data} onSaved={load} />}
        {data && <SendMessage />}
        {data && <Reports />}
        {data && <RecentAlerts data={data} />}
      </div>
    </div>
  );
}
